import { useMemo, useState } from "react"
import { ChevronDown, ChevronRight, Database } from "lucide-react"
import { cn } from "@/lib/utils"

interface FullProfilePanelProps {
  profile: Record<string, unknown>
  className?: string
}

// Fields that are too noisy or sensitive to show in the flat key/value view
// — they get rendered specially or under their own collapsible section.
const NESTED_FIELDS = new Set([
  "onboarding_data",
  "training_schedule",
  "preferred_days",
  "goals",
  "selected_activities",
  "studio_preferences",
  "feature_flags",
  "agent_overrides",
  "mindbody_oauth",
  "equipment",
])

const SENSITIVE_FIELDS = new Set(["mindbody_oauth", "push_token"])

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "boolean") return value ? "true" : "false"
  if (typeof value === "number") return String(value)
  if (typeof value === "string") {
    if (!value) return "—"
    if (value.length > 120) return value.slice(0, 120) + "…"
    return value
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]"
    return `${value.length} item${value.length === 1 ? "" : "s"}`
  }
  if (typeof value === "object") {
    const keys = Object.keys(value as object)
    if (keys.length === 0) return "{}"
    return `${keys.length} field${keys.length === 1 ? "" : "s"}`
  }
  return String(value)
}

function CollapsibleJson({
  label,
  value,
  defaultOpen = false,
  redact = false,
}: {
  label: string
  value: unknown
  defaultOpen?: boolean
  redact?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const summary = formatValue(value)
  const Icon = open ? ChevronDown : ChevronRight

  if (value === null || value === undefined) {
    return (
      <div className="flex items-start gap-2 py-1 text-sm">
        <span className="text-muted-foreground min-w-[160px] font-mono text-xs">
          {label}
        </span>
        <span className="text-muted-foreground">—</span>
      </div>
    )
  }

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-start gap-2 py-1.5 text-left text-sm hover:bg-muted/50 px-2 -mx-2 rounded"
      >
        <Icon className="size-3.5 mt-0.5 text-muted-foreground shrink-0" />
        <span className="text-muted-foreground min-w-[160px] font-mono text-xs">
          {label}
        </span>
        <span className="font-medium text-xs">{summary}</span>
      </button>
      {open && (
        <pre className="mt-1 mb-2 ml-6 rounded bg-muted/50 p-3 text-xs font-mono whitespace-pre-wrap break-all max-h-96 overflow-auto">
          {redact
            ? "(redacted — sensitive value, view in DB if needed)"
            : JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  )
}

function FlatField({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex items-start gap-2 py-1 text-sm">
      <span className="text-muted-foreground min-w-[160px] font-mono text-xs">
        {label}
      </span>
      <span className="font-medium wrap-break-word">{formatValue(value)}</span>
    </div>
  )
}

export function FullProfilePanel({
  profile,
  className,
}: FullProfilePanelProps) {
  const [collapsed, setCollapsed] = useState(false)

  const { flatEntries, nestedEntries } = useMemo(() => {
    const flat: [string, unknown][] = []
    const nested: [string, unknown][] = []
    const keys = Object.keys(profile).sort()
    for (const k of keys) {
      if (NESTED_FIELDS.has(k)) {
        nested.push([k, profile[k]])
      } else {
        flat.push([k, profile[k]])
      }
    }
    return { flatEntries: flat, nestedEntries: nested }
  }, [profile])

  return (
    <div className={cn("rounded-lg border bg-card", className)}>
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-muted/50 rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <Database className="size-4 text-muted-foreground" />
          <h3 className="font-semibold">
            Full profile (everything the AI sees)
          </h3>
        </div>
        {collapsed ? (
          <ChevronRight className="size-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-4 text-muted-foreground" />
        )}
      </button>

      {!collapsed && (
        <div className="border-t p-4 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Scalar fields
            </div>
            <div className="space-y-0.5">
              {flatEntries.map(([k, v]) => (
                <FlatField key={k} label={k} value={v} />
              ))}
            </div>
          </div>

          {nestedEntries.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Nested fields (click to expand)
              </div>
              <div>
                {nestedEntries.map(([k, v]) => (
                  <CollapsibleJson
                    key={k}
                    label={k}
                    value={v}
                    defaultOpen={k === "onboarding_data"}
                    redact={SENSITIVE_FIELDS.has(k)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
